/* NODE MODULES */
import React, { useEffect, useState } from "react";
import debounce from "debounce";
import moment from "moment";

/* CONTEXT */

/* UI APP SHELL */

/* UI DESIGN SYSTEM */
import {
    Upload,
    Icon,
    message,
    Select,
    Row,
    Col,
    Card,
    Button,
    Modal,
    Table,
    Popconfirm
} from 'antd';

/* UI COMPONENTS */

/* SERVICES */
import {
    _getManySuppliers,
    _postDuplicateProductsExcel,
    _getDuplicateProductsExcel,
    _getManyDuplicateProductJobProgressDtos,
    _getUser,
    _postDuplicateProductJobDeleteDuplicatedProducts
} from "../../../services/index";

import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";

/* UTILS & CONFIG */
import { getRandomKey } from "../../../utils/index";

/* TYPES */
/** @typedef Template T */
/** @typedef {import ("antd/lib/table").ColumnProps<T>} ColumnProps */
/** @typedef {import('../../../services/index'.T_DuplicateProductJobProgressDTO} T_DuplicateProductJobProgressDTO */
/** @typedef {import("../../../services/index".T_DuplicateProductJobValidationResponse} T_DuplicateProductJobValidationResponse */
/** @typedef {import("antd/lib/table").PaginationConfig} PaginationConfig */
/** @typedef {"CLOSED" | "UPLOADING" | "ERROR" | "SUCCESS"} T_DuplicateProductJobsModalConfig */


export function MassDuplicateProducts(props) {

    /* REACT HOOK */

    /* STATE */
    // Users -> Confirmed Destination Suppliers and Eezee Admin
    const [users, setUsers] = useState([]);

    // Suppliers -> Suppliers that are found on search
    const [suppliers, setSuppliers] = useState([]);
    const [destinationSupplier, setDestinationSupplier] = useState(null);
    const [searchParams, setSearchParams] = useState("");
    /** @type {[ T_DuplicateProductJobValidationResponse | null, React.Dispatch<T_DuplicateProductJobValidationResponse> ]} */
    const [uploadErrorObject, setUploadErrorObject] = useState(null);
    /** @type {[T_DuplicateProductJobProgressDTO[], React.Dispatch<T_DuplicateProductJobProgressDTO[]> ]} */
    const [duplicateProductJobProgressObjects, setDuplicateProductJobProgressObjects] = useState(
        []
    );
    /** @type {[PaginationConfig, React.Dispatch<PaginationConfig>]} */
    const [paginationConfig, setPaginationConfig] = useState(
        /** @type {PaginationConfig} */
        {
            pageSize: 5,
            current: 1,
            total: 5
        }
    );
    const [isTableLoading, setIsTableLoading] = useState(false);
    /** @type {[T_DuplicateProductJobsModalConfig, React.Dispatch<T_DuplicateProductJobsModalConfig>]} */
    const [modalConfig, setModalConfig] = useState(
        "CLOSED"
    );
    const [uploadRandomKey, setUploadRandomKey] = useState(getRandomKey());


    const state = {
        users,
        suppliers,
        destinationSupplier,
        searchParams,
        /** @type {T_DuplicateProductJobValidationResponse | null}  */
        uploadErrorObject,
        duplicateProductJobProgressObjects,
        paginationConfig,
        isTableLoading,
        /** @type {T_DuplicateProductJobsModalConfig} */
        modalConfig,
        uploadRandomKey
    };
    const stateMethod = {
        setUsers,
        setSuppliers,
        setDestinationSupplier,
        setSearchParams,
        setUploadErrorObject,
        setDuplicateProductJobProgressObjects,
        setPaginationConfig,
        setIsTableLoading,
        setModalConfig,
        setUploadRandomKey
    };

    /* METHOD <RENDERER, GETTER, SETTER, EVENT HANDLER> */
    const method = {
        /** @returns {ColumnProps<T_DuplicateProductJobProgressDTO>[]} */
        getColumns: () => {


            return [
                {
                    title: "Created At",
                    key: "createdAt",
                    dataIndex: "createdAt",
                    /** 
                     * @param {T_DuplicateProductJobProgressDTO} record
                     * @returns {React.ReactNode}  
                     */
                    render: (_, record) => {
                        const timestamp = 'MMMM DD, YYYY h:mm A';
                        return moment(record.duplicateProductJob.createdAt).format(timestamp);
                    }
                },
                {
                    title: "Uploaded By",
                    key: "createdByUserId",
                    dataIndex: "createdByUserId",
                    /** 
                     * @param {T_DuplicateProductJobProgressDTO} record
                     * @returns {React.ReactNode}  
                     */
                    render: (_, record) => {
                        const relevantUser = users.filter(
                            adminUser => adminUser._id === record.duplicateProductJob.createdByUserId
                        )[0];
                        return relevantUser ?
                            relevantUser.local.email :
                            `Loading...`;
                    }
                },
                {
                    title: "Destination Supplier",
                    key: "destinationSupplierId",
                    dataIndex: "destinationSupplierId",
                    /** 
                     * @param {T_DuplicateProductJobProgressDTO} record
                     * @returns {React.ReactNode}  
                     */
                    render: (_, record) => {
                        const relevantUser = users.filter(
                            adminUser => adminUser._id === record.duplicateProductJob.destinationSupplierId
                        )[0];
                        return relevantUser ?
                            relevantUser.local.email :
                            `Loading...`;
                    }
                },
                {
                    title: "Progress",
                    key: "progress",
                    dataIndex: "progress",
                    /** 
                     * @param {T_DuplicateProductJobProgressDTO} record
                     * @returns {React.ReactNode}  
                     */
                    render: (_, record) => {
                        return (
                            <Row
                                type="flex">
                                <Col>
                                    {record.completedLineItemsCount}
                                </Col>
                                <Col>
                                    <Text style={{ marginLeft: "4px", marginRight: "4px" }}>
                                        /
                                    </Text>
                                </Col>
                                <Col>
                                    {record.totalLineItemsCount}
                                </Col>
                            </Row>
                        );
                    }
                },
                {
                    title: "Actions",
                    key: "action",
                    dataIndex: "action",
                    /** 
                     * @param {T_DuplicateProductJobProgressDTO} record
                     * @returns {React.ReactNode}  
                     */
                    render: (_, record) => {
                        const markedToDeleteDuplicatedProductsDate = record.duplicateProductJob.markedToDeleteDuplicatedProductsDate
                        const isMarkedToDeleteDuplicatedProducts = Boolean(markedToDeleteDuplicatedProductsDate)
                        const textType = isMarkedToDeleteDuplicatedProducts 
                            ? "secondary"
                            : "danger"
                        return (
                            <Popconfirm
                                disabled={isMarkedToDeleteDuplicatedProducts}
                                title="Confirm to remove duplicated products?"
                                onConfirm={() => method.handleOnClickDeleteDuplicatedProducts(record.duplicateProductJob._id)}>
                                <Button 
                                    type="link" 
                                    style={{ padding: 0 }} 
                                    disabled={isMarkedToDeleteDuplicatedProducts}>
                                    <Text type={textType}>
                                        Delete Duplicated Products
                                    </Text>
                                </Button>
                            </Popconfirm>
                        );
                    }
                },
            ];
        },
        // @todo - need to integrate handling of pagination in this method also
        /** @param {{
         *      pageNumber: number,
         *      pageSize: number
         * }} paginationConfig */
        getAndSetDuplicateProductJobsTableData: async (paginationConfig) => {
            stateMethod.setIsTableLoading(true);
            const duplicateProductJobsXhr = await _getManyDuplicateProductJobProgressDtos(
                {
                    pageNumber: paginationConfig.pageNumber,
                    pageSize: paginationConfig.pageSize,
                }
            );
            stateMethod.setDuplicateProductJobProgressObjects(duplicateProductJobsXhr.data.data);

            /* Stop loading once duplicate product jobs have loaded */
            setTimeout(() => {
                stateMethod.setIsTableLoading(false);
            }, 400);

            stateMethod.setPaginationConfig(
                {
                    current: paginationConfig.pageNumber,
                    pageSize: paginationConfig.pageSize,
                    total: duplicateProductJobsXhr.data.totalRecordCount
                }
            );

            const tableUserIds = [];
            duplicateProductJobsXhr.data.data.forEach(
                duplicateProductJobPaginateResult => {
                    const adminUserId = duplicateProductJobPaginateResult.duplicateProductJob.createdByUserId;
                    const supplierUserId = duplicateProductJobPaginateResult.duplicateProductJob.destinationSupplierId;
                    if (adminUserId && !tableUserIds.includes(adminUserId)) {
                        tableUserIds.push(adminUserId);
                    }
                    if (supplierUserId && !tableUserIds.includes(supplierUserId)) {
                        tableUserIds.push(supplierUserId);
                    }
                }
            );

            /**
             * @todo - Do out route to query for bulk users
             */
            const _users = [];
            await Promise.all(
                tableUserIds.map(async adminUserId => {
                    const userXhr = await _getUser(adminUserId);
                    if (userXhr.data)
                        _users.push(userXhr.data);
                })
            );

            stateMethod.setUsers(_users);

        },
        getModalBody: () => {

            let modalBodyTitle = "";
            let text = "";
            let iconType = "";
            switch (state.modalConfig) {
                case "SUCCESS":
                    text = "Closing in 5 seconds...";
                    iconType = "check";
                    modalBodyTitle = "Upload Success";
                    break;
                case "ERROR": {
                    iconType = "exclamation-circle";
                    modalBodyTitle = "Upload Error";
                    const errorType = state.uploadErrorObject ?
                        state.uploadErrorObject.errorType :
                        null;
                    if (errorType === "DUPLICATE_OR_MISSING_INPUT") {
                        text = "There are duplicate or missing rows in your excel.";
                    }
                    if (errorType === "INVALID_PRODUCT_IDS") {
                        text = "Some product ids were found to be invalid.";
                    }
                    if (errorType === "INVALID_FILE_FORMAT") {
                        text = "Incorrect file format! .xlsx only.";
                    }
                    break;
                }
                case "UPLOADING": {
                    iconType = "loading";
                    text = "Loading...";
                    modalBodyTitle = "Uploading...";
                    break;
                }
                case "CLOSED":
                default:
                    break;
            }

            return (

                <Row type="flex" justify="center">
                    <Col>

                        <Row
                            type="flex"
                            justify="center">
                            <Title level={3}>
                                {modalBodyTitle}
                            </Title>

                        </Row>
                        <Row
                            type="flex"
                            justify="center">
                            <Col>
                                <Icon
                                    type={iconType}
                                    style={{ fontSize: 50 }}
                                />
                            </Col>
                        </Row>

                        <div style={{ height: "16px" }} />

                        <Row
                            type="flex"
                            justify="center">
                            <Col>
                                <Text>
                                    {text}
                                </Text>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            );
        },
        getModalFooter: () => {

            function handleOnClickOk() {
                stateMethod.setModalConfig("CLOSED");
                stateMethod.setUploadErrorObject(null);
            }

            function handleOnDownloadErrorXlsx() {

                if (!state.uploadErrorObject || !state.uploadErrorObject.errorXlsxUrl) {
                    return;
                }

                const href = state.uploadErrorObject.errorXlsxUrl;
                const fileName = `duplicate-products-error-${new Date().getTime()}.xlsx`;

                const anchorElement = document.createElement("a");
                anchorElement.href = href;
                anchorElement.download = fileName;

                document.body.appendChild(anchorElement);
                anchorElement.click();

                document.body.removeChild(anchorElement);
                window.URL.revokeObjectURL(href);
            }

            return (
                <div>
                    {state.modalConfig === "ERROR" && (
                        <Button
                            type="danger"
                            onClick={handleOnDownloadErrorXlsx}>
                            Open Error .xlsx
                        </Button>
                    )}
                    <Button
                        style={{ marginLeft: "8px" }}
                        type="primary"
                        onClick={handleOnClickOk}
                    >
                        Close
                    </Button>

                </div>
            );
        },
        /**
         * @param {string} value
         */
        doSearch: async (value) => {
            const suppliersXhr = await _getManySuppliers(
                {
                    pageNumber: 1,
                    pageSize: 20,
                    searchParams: value
                }
            );
            stateMethod.setSuppliers(suppliersXhr.data.data);

        },
        handleOnClickDownloadTemplate: async () => {

            const duplicateProductsExcelXhr = await _getDuplicateProductsExcel();
            const href = window.URL.createObjectURL(duplicateProductsExcelXhr.data);
            const fileName = `duplicate-products-template-${new Date().getTime()}.xlsx`;

            const anchorElement = document.createElement("a");
            anchorElement.href = href;
            anchorElement.download = fileName;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        /** @param {string} value */
        handleOnSelectSupplier: (value) => {
            const relevantSupplier = state.suppliers.filter(supplier => supplier._id === value)[0];
            if (!relevantSupplier) {
                console.error("Relevant Supplier not found!");
                return;
            }
            stateMethod.setDestinationSupplier(relevantSupplier);
        },
        handleOnChangeInput: (value) => {

            stateMethod.setSearchParams(value);
            method.doSearch(value);
        },
        /** @param {string} duplicatedProductJobId */
        handleOnClickDeleteDuplicatedProducts: async (
            duplicatedProductJobId
        ) => {
            await _postDuplicateProductJobDeleteDuplicatedProducts({
                duplicatedProductJobId,
            });
            // refresh
            console.log("CALLED REFRESH");
            await method.getAndSetDuplicateProductJobsTableData({
                pageNumber: state.paginationConfig.current,
                pageSize: state.paginationConfig.pageSize
            });
        },
        /** @param {{
         *      pageSize: number,
         *      current: number,
         *      total: number
         * }} paginationConfig
         * */
        handleOnChangePageNumber: async (paginationConfig) => {
            await method.getAndSetDuplicateProductJobsTableData({
                pageNumber: paginationConfig.current,
                pageSize: state.paginationConfig.pageSize
            });
        }
    };

    /* CUSTOM HOOKS */

    /* VARIABLES */

    /* CSS CLASSNAME */

    /* COMPONENT DID MOUNT || DID UPDATE */
    method.handleOnChangeInput = debounce(method.handleOnChangeInput, 400);
    useEffect(() => {
        (async () => {
            const [
                suppliersXhr
            ] = await Promise.all(
                [
                    _getManySuppliers(
                        {
                            pageNumber: 1,
                            pageSize: 20,
                            searchParams: ""
                        }
                    )
                ],
                method.getAndSetDuplicateProductJobsTableData({
                    pageSize: state.paginationConfig.pageSize,
                    pageNumber: state.paginationConfig.current
                })
            );
            stateMethod.setSuppliers(suppliersXhr.data.data);

        })();

    }, []);

    return (
        <Card title="Mass Duplicate Products">

            <Text strong>
                Product Duplication Progress
            </Text>

            <Table
                onChange={method.handleOnChangePageNumber}
                loading={state.isTableLoading}
                columns={method.getColumns()}
                dataSource={state.duplicateProductJobProgressObjects}
                pagination={state.paginationConfig}
            />

            <Row>

                <Col>
                    <Text strong>
                        Destination Supplier
                    </Text>

                    <div style={{ marginTop: "4px" }} />

                    <Select
                        style={{ width: 700 }}
                        showSearch
                        autoFocus
                        beforeUpload={false}
                        filterOption={false}
                        // open
                        dropdownMenuStyle={{ height: "400px" }}
                        placeholder="Search by supplier id, email, company name."
                        onChange={method.handleOnSelectSupplier}
                        onSearch={method.handleOnChangeInput}
                        dropdownRender={(menu) => {

                            return (
                                <div>
                                    {menu}
                                    <div
                                        style={{
                                            textAlign: "center",
                                            backgroundColor: "#D7D8DB",
                                            height: 24,
                                        }}>
                                        <Text
                                            strong
                                            style={{ color: "#55585D" }}>
                                            Showing up to 20 suppliers.
                                        </Text>
                                    </div>
                                </div>
                            );
                        }}
                    >
                        {state.suppliers.map(supplier => {
                            return (
                                <Select.Option
                                    key={supplier._id}
                                    value={supplier._id}>
                                    <Row type="flex">
                                        <Col>
                                            <div
                                                style={{ width: 300 }}>
                                                <Text strong>
                                                    {supplier.details.companyName}
                                                </Text>
                                            </div>
                                        </Col>


                                        <Col>
                                            <div style={{ width: 200, marginLeft: 80 }}>
                                                <Text>
                                                    {supplier.details.email}
                                                </Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </Select.Option>
                            );
                        })}
                    </Select>

                </Col>

            </Row>

            <div style={{ marginTop: "8px" }} />

            <Text strong>
                Supplier ID
            </Text>

            <div style={{ marginTop: "4px" }} />

            <div style={{ height: "32px" }}>
                {state.destinationSupplier && (
                    <Text code style={{ fontSize: "14px" }}>
                        {state.destinationSupplier._id}
                    </Text>
                )}
            </div>


            <Text strong>
                Supplier Country
            </Text>

            <div style={{ marginTop: "4px" }} />

            <div style={{ height: "32px" }}>
                {state.destinationSupplier && (
                    <Text style={{ fontSize: "14px" }}>
                        {(state.destinationSupplier.shop.shopCountry || "SG")}
                    </Text>
                )}
            </div>


            <Row type="flex">
                <Col>
                    <Button type="primary"
                        onClick={method.handleOnClickDownloadTemplate}>
                        Download Template
                    </Button>
                </Col>
            </Row>

            <div style={{ marginTop: "16px" }} />


            <Upload.Dragger
                /* Don't show file preview */
                fileList={[]}
                customRequest={async (options) => {
                    try {

                        console.log("UPLOADING ", options);
                        if (!state.destinationSupplier) {
                            message.error("Select Destination Supplier first!");
                            return;
                        }

                        stateMethod.setModalConfig("UPLOADING");

                        const validationResponseXhr = await _postDuplicateProductsExcel({
                            file: options.file,
                            supplierId: state.destinationSupplier._id
                        });

                        if (!validationResponseXhr.data.success) {

                            stateMethod.setModalConfig("ERROR");
                            stateMethod.setUploadErrorObject(validationResponseXhr.data);
                            return;
                        }

                        stateMethod.setModalConfig('SUCCESS');
                        await method.getAndSetDuplicateProductJobsTableData({
                            pageNumber: 1,
                            pageSize: state.paginationConfig.pageSize
                        });
                        setTimeout(() => {
                            stateMethod.setModalConfig("CLOSED");
                        }, 5000);

                    } catch (e) {
                        console.error(e);
                    }

                }}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple={false}>
                <p className="ant-upload-drag-icon">
                    <Icon type="inbox" />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload.  XLSX files only.</p>
            </Upload.Dragger>

            { /* @todo Should add way to download error exccel*/}
            <Modal
                closable={false}
                visible={state.modalConfig !== "CLOSED"}
                footer={method.getModalFooter()}
            >
                {method.getModalBody()}

            </Modal>

        </Card>


    );
};


// WEBPACK FOOTER //
// ./src/components/product/mass-duplicate-products/mass-duplicate-products.js